import { Injectable } from '@angular/core';
import { Logger } from '../logger.service';
import { DateService } from './date.service';
import { addDays, setHours, startOfDay, isToday, setMinutes } from 'date-fns';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { AngularFirestore, DocumentReference } from '@angular/fire/compat/firestore';

const log = new Logger('general-service');

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  countDoc = 'count';
  constructor(private dateS: DateService, private http: HttpClient, private firestore: AngularFirestore) {}

  async counterRefCode(): Promise<number> {
    const ref = this.firestore.collection('lotenalCounter').doc(this.countDoc).ref;

    try {
      const newCounter = await this.firestore.firestore.runTransaction(async (transaction) => {
        const doc = await transaction.get(ref);
        const data = doc.data() as { counter: number } | undefined;

        let currentCounter = 0;

        if (doc.exists) {
          currentCounter = data?.counter ?? 0;
        }

        const updatedCounter = currentCounter + 1;
        transaction.update(ref, { counter: updatedCounter });

        return updatedCounter;
      });

      log.info(`Nuevo contador: ${newCounter}`);
      return newCounter;
    } catch (error) {
      log.error('Error actualizando el contador:', error);
      throw new Error('No se pudo obtener el contador.');
    }
  }

  generateNumber(min: number, max: number, exclude: number[] = []): number {
    // Validamos que el mínimo sea menor o igual al máximo
    if (min > max) {
      throw new Error('El mínimo debe ser menor o igual al máximo.');
    }

    // Creamos un conjunto para almacenar los números excluidos
    const conjuntoExcluidos = new Set(exclude);

    // Calculamos el rango posible (incluyendo los extremos)
    const rango = max - min + 1;

    // Generamos un número aleatorio dentro del rango total
    let numeroAleatorio = Math.floor(Math.random() * rango) + min;

    // Mientras el número generado esté en la lista de excluidos, generamos otro
    while (conjuntoExcluidos.has(numeroAleatorio)) {
      numeroAleatorio = Math.floor(Math.random() * rango) + min;
    }

    return numeroAleatorio;
  }

  async getDates(n?: number): Promise<Date[]> {
    if (typeof n === 'undefined') {
      n = this.generateNumber(1, 18);
    }
    const now = (await this.dateS.dateNow()).getTime();
    const dates = [];
    for (let i = 0; i < n; i++) {
      dates.push(new Date(this.generateNumber(now, now + 99999999999)));
    }
    return dates.sort((a, b) => a.getTime() - b.getTime());
  }

  waitOneSecond(seconds = 1): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1000 * seconds);
    });
  }

  /**
   * Obtiene un array de fechas basado en una cantidad específica y los días de la semana que deseas incluir.
   * @param quantity - número de fechas.
   * @param days - array de números que representan los días de la semana (0 = domingo, 1 = lunes, ..., 6 = sábado).
   * @param includeLastSunday - establece si se incluye el ultimo domingo del mes.
   * @param startDate - fecha opcional desde donde se desea comenzar la generación de fechas.
   * @returns Array de fechas.
   */
  async getDatesByDayWeek(
    quantity: number,
    days: number[],
    includeLastSunday: boolean = true,
    startDate?: Date
  ): Promise<Date[]> {
    const result = [];
    let currentDate = startDate ? new Date(startDate) : await this.dateS.dateNow(); // Comienza con la fecha actual o especificada
    let i = 0;
    let actualMonth = currentDate.getMonth();

    while (result.length < quantity) {
      // Verifica si el día actual está en la lista de días deseados

      if (days.includes(currentDate.getDay())) {
        const lastDayOfMonth = await this.getLastSundayOfMonth(quantity, startDate);

        // Si es último domingo del mes
        if (currentDate.getDay() === 0 && currentDate.getDate() === lastDayOfMonth[i].getDate()) {
          if (includeLastSunday) {
            result.push(new Date(currentDate)); // Incluir el último domingo
          }
        } else {
          result.push(new Date(currentDate)); // Añadir días normales
        }
      }

      // Avanza al siguiente día
      currentDate.setDate(currentDate.getDate() + 1);
      if (currentDate.getMonth() !== actualMonth) {
        actualMonth = currentDate.getMonth();
        i++;
      }
    }

    return result;
  }

  async getLastSundayOfMonth(quantity: number, startDate?: Date): Promise<Date[]> {
    const result = [];
    let currentDate = startDate ? new Date(startDate) : await this.dateS.dateNow(); // Comienza con la fecha actual o especificada

    while (result.length < quantity) {
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      // Iterar hacia atrás desde el último día
      let lastDayOfMonth = new Date(lastDay);
      while (lastDayOfMonth.getDay() !== 0) {
        // 0 representa domingo
        // if (lastDayOfMonth.getMonth() === 1) log.info(lastDayOfMonth.getDay());
        lastDayOfMonth.setDate(lastDayOfMonth.getDate() - 1);
      }

      result.push(new Date(lastDayOfMonth));

      // Pasar al siguiente mes
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return result;
  }

  splitArrayIntoChunks<T>(array: T[], chunkSize: number): T[][] {
    const result: T[][] = [];

    for (let i = 0; i < array.length; i += chunkSize) {
      const chunk = [...array.slice(i, i + chunkSize)];
      result.push([...chunk]);
    }

    return result;
  }

  stringToNumericHash(input: string): number {
    let hash = 0;

    // Recorrer cada carácter de la cadena
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i); // Obtener el código ASCII del carácter
      hash = (hash << 5) - hash + char; // Operación para generar un hash único
      hash |= 0; // Convertir a un entero de 32 bits
    }

    // Asegurarse de que el hash sea positivo
    return Math.abs(hash);
  }

  dictionary = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  encodeString(input: string): number {
    let encoded = 0;

    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      const index = this.dictionary.indexOf(char);
      if (index === -1) throw new Error('Carácter no válido');
      encoded = encoded * this.dictionary.length + index;
    }

    return encoded;
  }

  decodeNumber(encoded: number): string {
    let decoded = '';

    while (encoded > 0) {
      const index = encoded % this.dictionary.length;
      decoded = this.dictionary[index] + decoded;
      encoded = Math.floor(encoded / this.dictionary.length);
    }

    return decoded;
  }

  async generateMexicoCityDatesFromHours(hoursToAdd: number[], days: number, baseDate?: Date): Promise<Date[]> {
    const result: Date[] = [];
    const today = await this.dateS.dateNow(); // Fecha actual
    const start = baseDate ? baseDate : today;
    const startDate = startOfDay(start); // Comienza al inicio del día actual o base

    // Iterar por los próximos 5 días
    for (let day = 0; day < days; day++) {
      const currentDay = addDays(startDate, day); // Fecha del día actual + offset

      // Horas específicas a agregar: 13, 15, 17, 19, 21
      // const hoursToAdd = [13, 15, 17, 19, 21];

      for (const hour of hoursToAdd) {
        // Crear una fecha con la hora específica en UTC
        const dateInUTC = setHours(currentDay, hour);

        // Convertir la fecha a la zona horaria de la Ciudad de México
        const dateInMexicoCity = dateInUTC;

        // Asegurarse de que la fecha sea válida y no esté en el pasado
        if (!baseDate && isToday(today) && dateInMexicoCity < today) {
          continue; // Ignorar si es antes de la fecha/hora actual
        }

        // Convertir de nuevo a UTC para mantener consistencia
        const finalDate = dateInMexicoCity;

        result.push(finalDate);
      }
    }

    return result;
  }

  adjustDatesToMexicoCityTime(dates: Date[], hour: number, now: Date): Date[] {
    const ds = dates.map((date) => {
      // Convertir la fecha al tiempo de la Ciudad de México
      const dateInMexicoCity = date;

      // Establecer la hora a 21:00 (9 PM) en la zona horaria de la Ciudad de México
      const adjustedDateInMexicoCity = setHours(setMinutes(dateInMexicoCity, 0), hour);

      // Convertir la fecha de vuelta a UTC para mantener consistencia
      const zoned = adjustedDateInMexicoCity;

      return zoned;
    });

    return ds.filter((d) => d.getTime() > now.getTime());
  }

  private factorial(num: number): number {
    if (num === 0 || num === 1) return 1;
    let result = 1;
    for (let i = 2; i <= num; i++) {
      result *= i;
    }
    return result;
  }

  /**
   * Funcion para generar el numero de combinaciones posibles sin repeticiones
   * @param n Tamaño del array a evaluar
   * @param k Tamaño del subconjunto
   * @returns Numero de combinaciones posibles
   */
  calculateCombinations(n: number, k: number): number {
    if (k > n) {
      throw new Error('El valor de k no puede ser mayor que n.');
    }
    return this.factorial(n) / (this.factorial(k) * this.factorial(n - k));
  }

  printOrderedJson(jsonString: string): string {
    // Paso 1: Parsear el JSON string a un objeto JavaScript
    const jsonObject = JSON.parse(jsonString);

    // Paso 2: Crear una función para ordenar las propiedades del objeto
    function sortObject(obj: any): any {
      return Object.keys(obj)
        .sort()
        .reduce((result, key) => {
          result[key] = typeof obj[key] === 'object' ? sortObject(obj[key]) : obj[key];
          return result;
        }, {} as any);
    }

    // Paso 3: Ordenar el objeto
    const sortedJsonObject = sortObject(jsonObject);

    // Paso 4: Convertir el objeto ordenado de nuevo a una cadena JSON
    const orderedJsonString = JSON.stringify(sortedJsonObject, null, 2);

    // Paso 5: Imprimir el JSON ordenado en la consola
    log.info(orderedJsonString);
    return orderedJsonString;
  }

  //Utilizados en progoles y protouch
  getXmlAsJson(filepath: string): Observable<any> {
    return this.http.get(filepath, { responseType: 'text' }).pipe(
      map((xmlString: string) => {
        const jsonResult = this.xmlToJson(xmlString);

        // Asegurarse de que EventRecord sea un array
        if (jsonResult.EventRecord && !Array.isArray(jsonResult.EventRecord)) {
          jsonResult.EventRecord = [jsonResult.EventRecord];
        }

        // Si no hay eventos, asignar un array vacío
        const eventsArray = jsonResult.EventRecord ?? [];

        // Formatear la respuesta para extraer solo local_name y visitor_name
        const formattedResult = {
          draw_num: jsonResult['@attributes'].draw_num,
          events: eventsArray.map((event: any) => ({
            '@attributes': event['@attributes'],
            local_name: event['@attributes'].local_name,
            visitor_name: event['@attributes'].visitor_name,
          })),
        };

        return JSON.stringify(formattedResult);
      })
    );
  }

  xmlToJson(xmlString: string): any {
    const parser = new DOMParser();
    const xml = parser.parseFromString(xmlString, 'application/xml');

    function convert(node: Node): any {
      let obj: any = {};
      if (node.nodeType === 1) {
        // Element
        if ((node as Element).attributes.length > 0) {
          obj['@attributes'] = {};
          for (let i = 0; i < (node as Element).attributes.length; i++) {
            const attribute = (node as Element).attributes.item(i);
            if (attribute) {
              obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
            }
          }
        }
      } else if (node.nodeType === 3) {
        // Text
        return node.nodeValue?.trim() || '';
      }

      if (node.hasChildNodes()) {
        for (let i = 0; i < node.childNodes.length; i++) {
          const child = node.childNodes.item(i);
          const nodeName = child.nodeName;
          const content = convert(child);
          if (content) {
            if (!obj[nodeName]) {
              obj[nodeName] = content;
            } else {
              if (!Array.isArray(obj[nodeName])) {
                obj[nodeName] = [obj[nodeName]]; // Convertir a array si hay más de un elemento
              }
              obj[nodeName].push(content);
            }
          }
        }
      }
      return obj;
    }

    const jsonResult = convert(xml.documentElement);

    // Asegurarse de que EventRecord sea un array
    if (jsonResult.EventRecord && !Array.isArray(jsonResult.EventRecord)) {
      jsonResult.EventRecord = [jsonResult.EventRecord];
    }

    return jsonResult;
  }
  getJsonData(filePath: string): Observable<any> {
    return this.getXmlAsJson(filePath).pipe(
      map((jsonString: string) => {
        return JSON.parse(jsonString);
      }),
      catchError((error) => {
        console.error('Error al obtener o procesar el JSON: ', error);
        throw error;
      })
    );
  }

  getRef<T>(id: string, collection: string): DocumentReference<T> {
    return this.firestore.collection<T>(collection).doc(id).ref;
  }

  // Función para generar un array con patrones de letras
  generatePatternArray(
    arrayBase: any[], // Array original que servirá como base para generar el nuevo array
    letrasPermitidas: string[] // Letras que se usarán para generar el patrón (ejemplo: ['a', 'b'], ['A', 'B', 'C'], etc.)
  ): string[] {
    // Validar que el array de letras permitidas no esté vacío
    if (letrasPermitidas.length === 0) {
      log.error('El array de letras permitidas no puede estar vacío.');
      return [];
    }

    // Crear un array vacío para almacenar el resultado
    const resultado: string[] = [];

    // Iterar sobre cada elemento del array base
    for (let i = 0; i < arrayBase.length; i++) {
      // Calcular el índice de la letra a usar dentro del array de letras permitidas
      // Usamos el operador módulo (%) para asegurarnos de que el índice no exceda el tamaño del array de letras
      const indiceLetra = i % letrasPermitidas.length;

      // Obtener la letra correspondiente
      const letra = letrasPermitidas[indiceLetra];

      // Determinar si necesitamos agregar un número al final de la letra
      // Dividimos el índice entre el tamaño del array de letras permitidas para obtener el "grupo"
      const grupo = Math.floor(i / letrasPermitidas.length);

      // Construir el elemento final
      // Si estamos en el primer grupo (grupo === 0), solo usamos la letra
      // Si estamos en un grupo posterior, agregamos el número del grupo al final de la letra
      const elemento = grupo === 0 ? letra : `${letra}${grupo + 1}`;

      // Agregar el elemento al array de resultados
      resultado.push(elemento);
    }

    // Retornar el array generado
    return resultado;
  }

  obtenerLetrasAleatorias(numeroDeLetras: number): string {
    // Paso 1: Validar que el número de letras sea mayor a 0.
    if (numeroDeLetras <= 0) {
      throw new Error('El número de letras debe ser mayor a 0.');
    }

    // Paso 2: Inicializar una variable para almacenar las letras generadas.
    let resultado = '';

    // Paso 3: Usar un bucle para generar el número de letras solicitado.
    for (let i = 0; i < numeroDeLetras; i++) {
      // Generar una letra al azar siguiendo la misma lógica que antes.
      const esMayuscula = Math.random() < 0.5; // Decidir si será mayúscula o minúscula.
      const indiceAleatorio = Math.floor(Math.random() * 26); // Índice entre 0 y 25.
      const codigoAsciiBase = esMayuscula ? 65 : 97; // Base ASCII para mayúsculas o minúsculas.
      const codigoAsciiLetra = codigoAsciiBase + indiceAleatorio;
      const letraAleatoria = String.fromCharCode(codigoAsciiLetra);

      // Agregar la letra generada al resultado.
      resultado += letraAleatoria;
    }

    // Paso 4: Retornar la cadena de letras generadas.
    return resultado;
  }
  getLocalISOString() {
    const now = new Date();
    const offsetInMinutes = now.getTimezoneOffset();
    const localTime = new Date(now.getTime() - offsetInMinutes * 60000);
    return localTime.toISOString().slice(0, -1); // quita la 'Z'
  }
}
